import React, {useEffect, useState} from 'react';
import Table from 'react-bootstrap/Table';
import {FaAngleDown, FaAngleUp, FaTrash} from 'react-icons/fa';
import {
    ActionTypeEnum,
    DailyTransactionTypeEnum,
    PosTransactionChangeTypeEnum,
     RequestActionModel,
    RowStateEnum,
    TransactionItemResponseModel
} from "../../../models";
import {getLabelName, isArabicCurrentLanguage} from "../../../utils";
import {
    accordionLabelStyle,
    accordionRowStyle,
    iconBtnStyle,
    popupActionsStyle,
    popupBoxStyle,
    popupBtnStyle,
    popupCancelBtnStyle,
    popupContainerStyle,
    popupDelBtnStyle,
    popupInputValueStyle,
    popupOkBtnStyle,
    popupRowStyle,
    qtyBtnStyle,
    qtyInputStyle,
    quantityCellStyle,
    tableHeadColoredStyle,
    tableHeadEmptyStyle,
    tableItemCellStyle,
} from "../common/css/posCss";
import {usePosTransactionsState} from "./helper/state/posTransactionsState";
import {getTextBoxNumericOrDefaultValue} from "../common/businessLogic/transactionBl";


interface PosTransactionItemsTableProps  {
    data: TransactionItemResponseModel[];
    transactionTypeId: DailyTransactionTypeEnum;
    handleAction: (request: RequestActionModel) => Promise<void>;
}

export const PosTransactionItemsTable: React.FC<PosTransactionItemsTableProps> = ({
                                                                                      data,
                                                                                      transactionTypeId,
                                                                                      handleAction
                                                                                  }) => {
    //#region state
    const {
        posSetting,
    } = usePosTransactionsState(transactionTypeId);
    const [isMobile, setIsMobile] = useState(window.innerWidth < 800);
    const [isNarrow, setIsNarrow] = useState(window.innerWidth < 580);
    const [expandedRow, setExpandedRow] = useState<string | null>(null);
    const [popupRowKey, setPopupRowKey] = useState<string | null>(null);
    const [inputValue, setInputValue] = useState<string>('');
    //#endregion

    //#region useEffect
    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth < 800);
            setIsNarrow(window.innerWidth < 580);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);
    //#endregion

    //#region functions
    const toggleAccordion = (rowKey: string) => {
        setExpandedRow(expandedRow === rowKey ? null : rowKey);
    };

    const handleDelete = async (row: TransactionItemResponseModel) => {
        if (posSetting.AllowDeleteItems) {
            await handleAction({
                action: ActionTypeEnum.UpdateTransactionItem,
                request: {
                    updatedRow: { ...row, rowState: RowStateEnum.Delete },
                    changeType: PosTransactionChangeTypeEnum.ItemDeleted
                }
            });
        } else {
            await handleAction({
                action: ActionTypeEnum.DeleteTransactionItem,
                request: row
            });
        }
    };

    const handleQuantityChange = async (
        row: TransactionItemResponseModel,
        action: 'increment' | 'decrement'
    ) => {
        const newQuantity = action === 'increment' ? row.Quantity + 1 : Math.max(1, row.Quantity - 1);
        await handleAction({
            action: ActionTypeEnum.UpdateItemQuantity,
            request: {
                updatedRow: { ...row, Quantity: newQuantity },
                changeType: PosTransactionChangeTypeEnum.QuantityChanged
            }
        });
    };
    const handleItemChange = async (
        row: TransactionItemResponseModel
    ) => {
        await handleAction({
            action: ActionTypeEnum.UpdateTransactionItem,
            request: {
                updatedRow: { ...row },
                changeType: PosTransactionChangeTypeEnum.DiscountChanged
            }
        });
    };

    //#endregion

    return (
        <Table bordered className="text-center align-middle">
            <thead>
            <tr className="custom-header">
                <th style={tableHeadEmptyStyle}></th>
                <th style={tableHeadColoredStyle}>{getLabelName("Item")}</th>
                <th style={tableHeadColoredStyle}>{getLabelName("Quantity")}</th>
                <th style={tableHeadColoredStyle}>{getLabelName("Price")}</th>
                <th style={tableHeadColoredStyle}>{getLabelName("Total")}</th>
                <th style={tableHeadEmptyStyle}></th>
            </tr>
            </thead>
            <tbody>
            {data.map((row, index) => (
                <React.Fragment key={`${row.rowKey}-${index}`}>
                    <tr>
                        <td>
                            <button style={iconBtnStyle} onClick={() => handleDelete(row)} id={`pos_delete_btn_${index}`}>
                                <FaTrash className="text-danger" />
                            </button>
                        </td>
                        <td style={tableItemCellStyle}>
                            {isArabicCurrentLanguage() ? row.ItemName : row.ItemName_En}
                        </td>
                        <td>
                            <div style={quantityCellStyle}>
                                {isNarrow && <button style={qtyBtnStyle} onClick={() => handleQuantityChange(row, 'decrement')} id={`pos_decrement_btn_${index}`}>-</button>}
                                <input
                                    id={`pos_item_qty_${index}`}
                                    value={row.Quantity}
                                    readOnly
                                    style={qtyInputStyle}
                                    onClick={() => {
                                        setPopupRowKey(row.rowKey);
                                        setInputValue(row.Quantity.toString());
                                    }}
                                />
                                {isNarrow && <button style={qtyBtnStyle} onClick={() => handleQuantityChange(row, 'increment')} id={`pos_increment_btn_${index}`}>+</button>}
                            </div>
                        </td>
                        <td>
                            <div style={quantityCellStyle}>
                                <input
                                    id={`pos_item_priceWithTax_${index}`}
                                    value={row.UnitPriceWithTaxInCurrency}
                                    // readOnly
                                    style={qtyInputStyle}
                                    type="number"
                                    step={"0.01"}
                                    min={"0"}
                                    onWheel={(event) => {
                                        event.currentTarget.blur();
                                    }}
                                    onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>)=>{
                                        if (event.key === "-" || event.key === "e") {
                                            event.preventDefault();
                                            return;
                                        }
                                    }}
                                    onChange={async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)=>{
                                        row.UnitPriceWithTaxInCurrency=getTextBoxNumericOrDefaultValue(e)
                                        await  handleItemChange(row)
                                    }}
                                />
                            </div>
                        </td>
                        <td>{row.Total.toFixed(2)}</td>
                        <td>
                            <button style={iconBtnStyle} onClick={() => toggleAccordion(index.toString())}>
                                {expandedRow === index.toString() ? <FaAngleUp /> : <FaAngleDown />}
                            </button>
                        </td>
                    </tr>
                    {expandedRow === index.toString() && (
                        <tr>
                            <td colSpan={5} style={accordionRowStyle}>
                                <div>
                                    <p  style={accordionLabelStyle}>{getLabelName("Discount")}:
                                        <input
                                            id={`pos_item_discount_${index}`}
                                            value={row.ItemDiscountInCurrency}
                                            style={qtyInputStyle}
                                            type="number"
                                            step={"0.01"}
                                            min={"0"}
                                            onWheel={(event) => {
                                                event.currentTarget.blur();
                                            }}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>)=>{
                                                if (event.key === "-" || event.key === "e") {
                                                    event.preventDefault();
                                                    return;
                                                }
                                            }}
                                            onChange={async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)=>{
                                                row.ItemDiscountInCurrency=getTextBoxNumericOrDefaultValue(e)
                                                 await  handleItemChange(row)
                                            }}
                                            onBlur={async (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>)=>{}}
                                            // onClick={() => {
                                            //     setPopupRowKey(row.rowKey);
                                            //     setInputValue(row.ItemDiscount.toString());
                                            // }}
                                        />
                                    </p>
                                    <p style={accordionLabelStyle}>{getLabelName("Price")}:<span>{row.Unit_PriceInCurrencyCalculated} </span></p>
                                    <p style={accordionLabelStyle}>{getLabelName("Price with tax")}:<span>{row.UnitPriceWithTaxInCurrency} </span></p>
                                    {/*<p style={accordionLabelStyle}>{getLabelName("Discount")}:<span>{row.ItemDiscount} </span></p>*/}
                                </div>
                            </td>
                        </tr>
                    )}
                    {!isNarrow && popupRowKey === row.rowKey && (
                        <tr>
                            <td colSpan={5} style={popupRowStyle}>
                                <div style={popupContainerStyle}>
                                    <div style={{ ...popupBoxStyle, width: isMobile ? '300px' : '200px' }}>
                                        <div style={popupInputValueStyle}>{inputValue}</div>
                                        {Array.from({ length: 10 }, (_, i) => i).map((num) => (
                                            <button
                                                key={num}
                                                onClick={() => setInputValue((prev) => prev + num.toString())}
                                                style={popupBtnStyle}
                                            >
                                                {num}
                                            </button>
                                        ))}
                                        <button style={popupDelBtnStyle} onClick={() => setInputValue((prev) => prev.slice(0, -1))}>DEL</button>
                                        <div style={popupActionsStyle}>
                                            <button
                                                style={popupCancelBtnStyle}
                                                onClick={() => {
                                                    setPopupRowKey(null);
                                                    setInputValue('');
                                                }}
                                            >Cancel</button>
                                            <button
                                                style={popupOkBtnStyle}
                                                onClick={async () => {
                                                    const updatedQuantity = parseInt(inputValue || '0', 10);
                                                    await handleAction({
                                                        action: ActionTypeEnum.UpdateItemQuantity,
                                                        request: {
                                                            updatedRow: {
                                                                ...row,
                                                                Quantity: updatedQuantity,
                                                            },
                                                            changeType: PosTransactionChangeTypeEnum.QuantityChanged
                                                        }
                                                    });
                                                    setPopupRowKey(null);
                                                    setInputValue('');
                                                }}
                                            >OK</button>
                                        </div>
                                    </div>
                                </div>
                            </td>
                        </tr>
                    )}
                </React.Fragment>
            ))}
            </tbody>
        </Table>
    );
};

//export default PosTransactionItemsTable;
